/**
 * avatars/aliens/population-generator.js
 * Động cơ tạo biến thể Quần thể (Population Generator).
 * Thuần logic, không phụ thuộc DOM/Three.js.
 */

import { SPECIES_IGNIS } from './species-ids.js';
import { CIVILIZED_SPECIES } from './species-data.js';

/**
 * Sinh cấu hình thành viên trong quần thể của một chủng tộc văn minh
 * @param {string} speciesId - Mã loài (ví dụ SPECIES_IGNIS, SPECIES_CYCLOPIA...)
 * @param {object} options - Tùy chọn dân số:
 *   - gender: 'male' | 'female' (mặc định 'male')
 *   - age: 'child' | 'adult' | 'elder' (mặc định 'adult')
 *   - colorIndex: 0..3 (chọn màu trong shirtPalette để đổi màu áo khi có 2 người cùng giới)
 * @returns {object} Cấu hình Avatar hoàn chỉnh cho BlockyAvatar
 */
export function getSpeciesPopulationMember(speciesId, options = {}) {
  const species =
    CIVILIZED_SPECIES[speciesId] ||
    Object.values(CIVILIZED_SPECIES).find((s) => s.speciesId === speciesId) ||
    CIVILIZED_SPECIES[SPECIES_IGNIS];

  const gender = options.gender === 'female' ? 'female' : 'male';
  const age = options.age === 'child' || options.age === 'elder' ? options.age : 'adult';
  const colorIndex = typeof options.colorIndex === 'number' && options.colorIndex >= 0 ? options.colorIndex : 0;

  const { dna, clothing, demographics } = species;
  const palette = clothing.shirtPalette || ['#0284c7', '#0369a1', '#0ea5e9', '#38bdf8'];
  const shirtColor = palette[colorIndex % palette.length];

  let bodyType = 'normal';
  let scale = 1.0;
  let hairStyle = demographics.maleHair || 'spiky';
  let hairColor = dna.hairColor ?? '#1e293b';
  let outfit = gender === 'female' ? clothing.femaleOutfit : clothing.maleOutfit;
  let beard = 'none';
  let horns = dna.horns || 'none';
  let wings = dna.wings || 'none';
  let tail = dna.tail || 'none';
  let scaleFactor = 1.0;
  let titleRole = '';

  if (age === 'child') {
    // Trẻ em: dáng nhỏ nhắn, sừng/vây scale nhỏ lại x0.6
    bodyType = 'child';
    scale = 0.88;
    hairStyle = demographics.childHair || 'short_neat';
    scaleFactor = 0.6;
    titleRole = gender === 'female' ? 'Bé Gái' : 'Bé Trai';
  } else if (age === 'elder') {
    // Người già: dáng hơi khom, tóc bạc, nam có râu elder_beard, sừng phong trần elder_horns
    bodyType = 'elder';
    scale = 0.98;
    hairColor = '#d8d8d8';
    hairStyle = demographics.elderHair || 'neat_parted';
    scaleFactor = 1.0;

    if (gender === 'male') {
      beard = 'elder_beard';
    }

    if (dna.horns && dna.horns !== 'none') {
      horns = 'elder_horns';
    }

    titleRole = gender === 'female' ? 'Trưởng Lão Bà' : 'Trưởng Lão';
  } else {
    // Người lớn: vóc dáng chuẩn, tóc theo giới tính
    bodyType = 'normal';
    scale = 1.0;
    hairStyle = gender === 'female' ? demographics.femaleHair || 'long_hair' : demographics.maleHair || 'spiky';
    scaleFactor = 1.0;
    titleRole = gender === 'female' ? 'Nữ Cư Dân' : 'Nam Cư Dân';
  }

  const memberId = `${species.speciesId}_${gender}_${age}_c${colorIndex}`;
  const memberName = `${species.name} (${titleRole})`;
  const defaultEyeColor = dna.eyeType === 'fire' ? '#f97316' : dna.eyeType === 'cyclops' ? '#22d3ee' : '#38bdf8';
  const eyeColor = dna.eyeColor ?? defaultEyeColor;
  const pantsColor = dna.pantsColor ?? '#1e293b';
  const shoesColor = dna.shoesColor ?? '#0f172a';

  return {
    id: memberId,
    name: memberName,
    title: `${species.planet} • ${titleRole}`,
    planet: species.planet,
    species: species.name,
    speciesId: species.speciesId,
    category: 'Cư dân văn minh',
    scale,
    bodyType,
    gender,
    age,
    colors: {
      skin: dna.skinColor || '#fcd5b5',
      hair: hairColor,
      shirt: shirtColor,
      pants: pantsColor,
      shoes: shoesColor,
      eyes: eyeColor,
      horns: dna.skinColor || '#ea580c',
      wings: shirtColor,
      tail: dna.skinColor || '#991b1b'
    },
    features: {
      outfit,
      hairStyle,
      eyeType: dna.eyeType || 'normal',
      hat: dna.hat || 'none',
      bodyAccessory: dna.bodyAccessory || 'none',
      horns,
      wings,
      tail,
      beard,
      scaleFactor,
      heldItem: 'none' // Tuyệt đối phi bạo lực
    },
    defaultReactions: species.reactions || []
  };
}
